import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  Clock,
  ShieldAlert,
  MessageSquare,
  Lock,
  ExternalLink,
  Send,
  Paperclip,
  CheckCircle2,
  History,
  Wifi,
  Globe,
  Monitor,
  Copy,
  Check,
  AlertCircle,
  UserCheck,
  Users,
  Sparkles,
  ArrowUpDown,
} from "lucide-react";
import {
  Issue,
  IssueStatus,
  Comment,
  InternalNote,
  UserProfile,
  IssueActivity,
} from "../types";
import {
  updateIssueStatus,
  assignIssue,
  addComment,
  subscribeToComments,
  addInternalNote,
  subscribeToInternalNotes,
  subscribeToActivities,
} from "../services/issueService";
import { getAppPillClasses } from "../utils/appColors";
import { CategoryBadge } from "./CategoryBadge";
import { ImageLightboxModal } from "./ImageLightboxModal";
import { getAttachmentDownloadUrl } from "../services/storageService";
import { openSafeAttachmentUrl } from "../utils/attachmentUtils";
import {
  formatSlaCountdown,
  calculateTimeToResolution,
} from "../utils/slaCalculator";
import { formatNetworkStatus } from "../utils/telemetry";
import { markTicketAsRead } from "../utils/notificationTracker";

export const formatCommentDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export interface StatusOptionConfig {
  value: IssueStatus;
  label: string;
  activeClass: string;
  inactiveClass: string;
  badgeClass: string;
}

export const STATUS_CONFIG: Record<IssueStatus, StatusOptionConfig> = {
  new: {
    value: "new",
    label: "New",
    activeClass:
      "bg-blue-600 text-white shadow-xs ring-2 ring-blue-300 border border-blue-600",
    inactiveClass:
      "bg-blue-50/70 text-blue-700 border border-blue-200 hover:bg-blue-100",
    badgeClass: "bg-blue-100 text-blue-800 border border-blue-200",
  },
  in_review: {
    value: "in_review",
    label: "In Review",
    activeClass:
      "bg-purple-600 text-white shadow-xs ring-2 ring-purple-300 border border-purple-600",
    inactiveClass:
      "bg-purple-50/70 text-purple-700 border border-purple-200 hover:bg-purple-100",
    badgeClass: "bg-purple-100 text-purple-800 border border-purple-200",
  },
  in_progress: {
    value: "in_progress",
    label: "In Progress",
    activeClass:
      "bg-teal-600 text-white shadow-xs ring-2 ring-teal-300 border border-teal-600",
    inactiveClass:
      "bg-teal-50/70 text-teal-700 border border-teal-200 hover:bg-teal-100",
    badgeClass: "bg-teal-100 text-teal-800 border border-teal-200",
  },
  pending_user_feedback: {
    value: "pending_user_feedback",
    label: "Pending Feedback",
    activeClass:
      "bg-amber-500 text-white shadow-xs ring-2 ring-amber-300 border border-amber-500",
    inactiveClass:
      "bg-amber-50/70 text-amber-700 border border-amber-200 hover:bg-amber-100",
    badgeClass: "bg-amber-100 text-amber-800 border border-amber-200",
  },
  resolved: {
    value: "resolved",
    label: "Resolved",
    activeClass:
      "bg-emerald-600 text-white shadow-xs ring-2 ring-emerald-300 border border-emerald-600",
    inactiveClass:
      "bg-emerald-50/70 text-emerald-700 border border-emerald-200 hover:bg-emerald-100",
    badgeClass: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  },
  closed: {
    value: "closed",
    label: "Closed",
    activeClass:
      "bg-slate-700 text-white shadow-xs ring-2 ring-slate-400 border border-slate-700",
    inactiveClass:
      "bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-200",
    badgeClass: "bg-slate-100 text-slate-700 border border-slate-300",
  },
};

export const STATUS_OPTIONS: StatusOptionConfig[] = [
  STATUS_CONFIG.new,
  STATUS_CONFIG.in_review,
  STATUS_CONFIG.in_progress,
  STATUS_CONFIG.pending_user_feedback,
  STATUS_CONFIG.resolved,
  STATUS_CONFIG.closed,
];

export interface IssueDetailModalProps {
  issue: Issue | null;
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile;
  adminUsers?: UserProfile[];
  onIssueUpdated?: (updatedIssue: Partial<Issue>) => void;
  onDeleteIssue?: (issueId: string) => void;
  initialStage?: "triage" | "detail";
}

interface AttachmentItem {
  path: string;
  url: string;
  name: string;
}

export const IssueDetailModal: React.FC<IssueDetailModalProps> = ({
  issue,
  isOpen,
  onClose,
  currentUser,
  adminUsers = [],
  onIssueUpdated,
  initialStage,
}) => {
  const isStaff =
    currentUser.role === "it_admin" || currentUser.role === "dept_admin";

  const [stage, setStage] = useState<"triage" | "detail">("detail");
  const [showColleaguePicker, setShowColleaguePicker] =
    useState<boolean>(false);

  const targetUrl = useMemo(() => {
    const rawUrl = issue?.sourceMetadata?.url;
    if (!rawUrl || typeof rawUrl !== "string") return null;
    const trimmed = rawUrl.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return null;
  }, [issue?.sourceMetadata?.url]);

  const [activeTab, setActiveTab] = useState<
    "overview" | "comments" | "notes" | "activity"
  >("overview");
  const [comments, setComments] = useState<Comment[]>([]);
  const [internalNotes, setInternalNotes] = useState<InternalNote[]>([]);
  const [activities, setActivities] = useState<IssueActivity[]>([]);
  const [commentInput, setCommentInput] = useState<string>("");
  const [noteInput, setNoteInput] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [previewAttachment, setPreviewAttachment] =
    useState<AttachmentItem | null>(null);
  const [copiedIp, setCopiedIp] = useState<boolean>(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [pendingStatusChange, setPendingStatusChange] =
    useState<IssueStatus | null>(null);
  const [commentSortOrder, setCommentSortOrder] = useState<"desc" | "asc">(
    "desc",
  );

  const backdropRef = useRef<HTMLDivElement | null>(null);

  // Initialize stage when opening issue
  useEffect(() => {
    if (isOpen && issue) {
      if (initialStage) {
        setStage(initialStage);
      } else if (isStaff && issue.status === "new" && !issue.assigneeUid) {
        setStage("triage");
      } else {
        setStage("detail");
      }
      setShowColleaguePicker(false);
    }
  }, [
    isOpen,
    issue?.id,
    initialStage,
    isStaff,
    issue?.status,
    issue?.assigneeUid,
  ]);

  // Mark ticket as read when opened
  useEffect(() => {
    if (isOpen && issue?.id && currentUser?.uid) {
      markTicketAsRead(issue.id, currentUser.uid);
    }
  }, [isOpen, issue?.id, currentUser?.uid]);

  // Sort comments according to selected order (defaults to most recent on top)
  const sortedComments = useMemo(() => {
    const allComments = [...comments];

    // Synthesize the description as the first comment
    if (issue) {
      const descriptionComment: Comment = {
        id: "description",
        issueId: issue.id,
        authorUid: issue.reporterUid,
        authorName: issue.reporterName,
        authorEmail: issue.reporterEmail,
        authorRole:
          issue.reporterUid === currentUser?.uid
            ? currentUser.role
            : "end_user",
        content: issue.description,
        createdAt: issue.createdAt,
      };
      allComments.push(descriptionComment);
    }

    return allComments.sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      return commentSortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });
  }, [comments, commentSortOrder, issue, currentUser?.uid, currentUser?.role]);

  // Sort internal notes defaulting to most recent on top
  const sortedInternalNotes = useMemo(() => {
    return [...internalNotes].sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime();
      const timeB = new Date(b.createdAt).getTime();
      return timeB - timeA;
    });
  }, [internalNotes]);

  const canAssign =
    currentUser.role === "it_admin" ||
    (currentUser.role === "dept_admin" &&
      issue !== null &&
      (currentUser.allowedApps?.includes(issue.appId) ||
        (issue.appId === "teppy-portal" &&
          currentUser.allowedApps?.includes("teppi-portal"))));

  const attachmentKey = issue?.attachmentPaths
    ? issue.attachmentPaths.join(",")
    : "";

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Load attachments
  useEffect(() => {
    let isMounted = true;
    if (
      !issue?.attachmentPaths ||
      issue.attachmentPaths.length === 0 ||
      !isOpen
    ) {
      setAttachments([]);
      return;
    }

    const loadAttachments = async () => {
      const items = await Promise.all(
        issue.attachmentPaths.map(async (path) => {
          try {
            if (
              path.startsWith("data:") ||
              path.startsWith("http://") ||
              path.startsWith("https://")
            ) {
              return { path, url: path, name: "Screenshot / Attachment" };
            }
            const url = await getAttachmentDownloadUrl(path);
            const name = path.split("/").pop() || "Attachment";
            return { path, url, name };
          } catch (err: unknown) {
            console.warn(
              "Could not load download URL for attachment:",
              path,
              err,
            );
            return null;
          }
        }),
      );
      if (isMounted) {
        setAttachments(
          items.filter((item): item is AttachmentItem => item !== null),
        );
      }
    };

    void loadAttachments();

    return () => {
      isMounted = false;
    };
  }, [issue?.id, attachmentKey, isOpen]);

  // Realtime subscriptions for comments, internal notes, and activities
  useEffect(() => {
    if (!issue || !isOpen) return;

    // 1. Public comments
    const unsubComments = subscribeToComments(issue.id, (loadedComments) => {
      setComments(loadedComments);
    });

    // 2. Activities (Audit Trail)
    const unsubActivities = subscribeToActivities(
      issue.id,
      (loadedActivities) => {
        setActivities(loadedActivities);
      },
    );

    // 3. Internal notes if staff
    let unsubNotes: (() => void) | null = null;
    if (isStaff) {
      unsubNotes = subscribeToInternalNotes(issue.id, (loadedNotes) => {
        setInternalNotes(loadedNotes);
      });
    }

    return () => {
      unsubComments();
      unsubActivities();
      if (unsubNotes) unsubNotes();
    };
  }, [issue, isOpen, isStaff]);

  if (!isOpen || !issue) return null;

  const slaInfo = issue.sla
    ? formatSlaCountdown(issue.sla.targetAssignTime)
    : null;

  const ttrMetrics = calculateTimeToResolution(issue);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) {
      onClose();
    }
  };

  const handleStatusButtonClick = (newStatus: IssueStatus) => {
    if (!issue || newStatus === issue.status || isUpdatingStatus) return;

    // For critical concluding states (closed, resolved), prompt for confirmation
    if (newStatus === "closed" || newStatus === "resolved") {
      setPendingStatusChange(newStatus);
      return;
    }

    void executeStatusChange(newStatus);
  };

  const executeStatusChange = async (newStatus: IssueStatus) => {
    if (!issue) return;
    setPendingStatusChange(null);
    setIsUpdatingStatus(true);
    const previousStatus = issue.status;

    try {
      await updateIssueStatus(issue.id, newStatus, currentUser, previousStatus);
      if (onIssueUpdated) {
        const nowIso = new Date().toISOString();
        const updates: Partial<Issue> = { status: newStatus };
        if (newStatus === "resolved") {
          updates.resolvedAt = nowIso;
          updates.closedAt = null;
        } else if (newStatus === "closed") {
          updates.closedAt = nowIso;
          updates.resolvedAt = nowIso;
        } else {
          updates.resolvedAt = null;
          updates.closedAt = null;
        }
        onIssueUpdated(updates);
      }
    } catch (err: unknown) {
      console.error("Failed to update issue status:", err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAssigneeChange = async (assigneeUid: string) => {
    try {
      const selectedAdmin = adminUsers.find((u) => u.uid === assigneeUid);
      const assigneeObj = selectedAdmin
        ? {
            uid: selectedAdmin.uid,
            email: selectedAdmin.email,
            displayName: selectedAdmin.displayName || selectedAdmin.email,
          }
        : null;

      await assignIssue(
        issue.id,
        assigneeObj,
        currentUser,
        issue.assigneeEmail,
      );
      if (onIssueUpdated) {
        onIssueUpdated({
          assigneeUid: assigneeObj?.uid ?? null,
          assigneeEmail: assigneeObj?.email ?? null,
        });
      }
    } catch (err: unknown) {
      console.error("Failed to assign issue:", err);
    }
  };

  const handlePostComment = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!commentInput.trim() || isSending) return;
    setIsSending(true);
    const content = commentInput;
    try {
      await addComment(issue.id, currentUser, content);
      setCommentInput("");

      if (issue.status === "new") {
        if (isStaff) {
          await executeStatusChange("in_review");
        } else if (onIssueUpdated) {
          onIssueUpdated({ status: "in_review" });
        }
      }
    } catch (err: unknown) {
      console.error("Failed to add comment:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleCommentKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Soft return (Shift + Enter): allow native newline insertion
        return;
      }
      e.preventDefault();
      if (!commentInput.trim() || isSending) return;
      void handlePostComment();
    }
  };

  const handlePostNote = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!noteInput.trim() || isSending) return;
    setIsSending(true);
    try {
      await addInternalNote(issue.id, currentUser, noteInput);
      setNoteInput("");
    } catch (err: unknown) {
      console.error("Failed to add internal note:", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Soft return (Shift + Enter): allow native newline insertion
        return;
      }
      e.preventDefault();
      if (!noteInput.trim() || isSending) return;
      void handlePostNote();
    }
  };

  const handleCopyIp = (ip: string) => {
    navigator.clipboard.writeText(ip);
    setCopiedIp(true);
    setTimeout(() => setCopiedIp(false), 2000);
  };

  const formatActivityTimestamp = (isoString: string) => {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const timeStr = date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
    return isToday
      ? `Today at ${timeStr}`
      : `${date.toLocaleDateString()} at ${timeStr}`;
  };

  const handleTriageAssign = async (assignee: {
    uid: string;
    email: string;
    displayName: string;
  }) => {
    if (!issue) return;
    setIsUpdatingStatus(true);
    try {
      await assignIssue(issue.id, assignee, currentUser, issue.assigneeEmail);
      // Auto-advance status to in_review per user decision
      await updateIssueStatus(issue.id, "in_review", currentUser, issue.status);
      if (onIssueUpdated) {
        onIssueUpdated({
          assigneeUid: assignee.uid,
          assigneeEmail: assignee.email,
          status: "in_review",
        });
      }
      setStage("detail");
    } catch (err: unknown) {
      console.error("Failed to assign ticket during triage:", err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <div
        ref={backdropRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-detail-title"
        onClick={handleBackdropClick}
        className="fixed inset-0 z-50 flex items-start justify-center px-3 sm:px-5 md:px-8 pt-4 sm:pt-8 md:pt-12 pb-4 sm:pb-8 bg-slate-900/50 backdrop-blur-sm overflow-y-auto"
      >
        <div className="w-full max-w-4xl h-[85vh] max-h-[calc(100vh-2.5rem)] sm:max-h-[calc(100vh-4rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          {stage === "triage" ? (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Stage 1 Triage Header */}
              <div className="p-5 sm:p-6 border-b border-slate-200 bg-slate-50/80 flex items-start justify-between gap-4 shrink-0">
                <div className="space-y-1.5 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-100 text-teal-800 border border-teal-200">
                      <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                      <span>New Ticket Triage</span>
                    </span>
                    <span className="font-mono text-xs font-bold text-slate-600 px-2 py-0.5 rounded bg-slate-100 border border-slate-200">
                      {issue.id}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border tracking-wide shadow-2xs ${getAppPillClasses(issue.appName)}`}
                    >
                      {issue.appName}
                    </span>
                    <CategoryBadge category={issue.category} size="md" />
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-semibold uppercase ${
                        issue.priority === "critical"
                          ? "bg-rose-100 text-rose-800"
                          : issue.priority === "high"
                            ? "bg-amber-100 text-amber-800"
                            : issue.priority === "medium"
                              ? "bg-sky-100 text-sky-800"
                              : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {issue.priority}
                    </span>
                  </div>
                  <h2
                    id="issue-detail-title"
                    className="text-lg sm:text-xl font-bold text-slate-900 leading-tight"
                  >
                    {issue.title}
                  </h2>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {targetUrl && (
                    <a
                      href={targetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open page URL: ${targetUrl}`}
                      aria-label="Open page URL in new tab"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors shrink-0 focus-visible:outline-2 focus-visible:outline-teal-500"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close dialog"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Stage 1 Triage Action Section (Near the top) */}
              <div className="p-5 sm:p-6 bg-slate-50/90 border-b border-slate-200 shrink-0 relative z-20">
                <div className="max-w-2xl mx-auto space-y-3">
                  <div className="text-center sm:text-left flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Who should take this ticket?
                      </h3>
                      <p className="text-xs text-slate-500">
                        Single click assigns the ticket and advances it to{" "}
                        <strong>In Review</strong>.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStage("detail")}
                      className="text-xs font-semibold text-teal-700 hover:text-teal-900 underline cursor-pointer"
                    >
                      Skip to full details →
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                    {/* 1. Assign to Me */}
                    <button
                      type="button"
                      disabled={isUpdatingStatus}
                      onClick={() =>
                        void handleTriageAssign({
                          uid: currentUser.uid,
                          email: currentUser.email,
                          displayName:
                            currentUser.displayName || currentUser.email,
                        })
                      }
                      className="p-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20 hover:shadow-lg transition-all flex flex-col items-center justify-center text-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs font-bold">Assign to Me</p>
                        <p className="text-[10px] text-teal-100 truncate max-w-[170px]">
                          {currentUser.displayName || currentUser.email}
                        </p>
                      </div>
                    </button>

                    {/* 2. Assign to Colleague */}
                    <div className="relative">
                      <button
                        type="button"
                        disabled={isUpdatingStatus}
                        onClick={() => setShowColleaguePicker((prev) => !prev)}
                        className={`w-full h-full p-4 rounded-xl border transition-all flex flex-col items-center justify-center text-center gap-1.5 cursor-pointer disabled:opacity-50 ${
                          showColleaguePicker
                            ? "bg-slate-900 text-white border-slate-900 shadow-md"
                            : "bg-white hover:bg-slate-50 text-slate-800 border-slate-300 shadow-xs"
                        }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            showColleaguePicker
                              ? "bg-white/20 text-white"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-xs font-bold">Someone Else</p>
                          <p
                            className={`text-[10px] ${
                              showColleaguePicker
                                ? "text-slate-300"
                                : "text-slate-500"
                            }`}
                          >
                            Select colleague
                          </p>
                        </div>
                      </button>

                      {showColleaguePicker && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowColleaguePicker(false)}
                          />
                          <div className="absolute top-full mt-2 left-0 right-0 sm:w-64 sm:right-auto bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 max-h-60 overflow-y-auto">
                            <p className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                              Select Assignee
                            </p>
                            {adminUsers
                              .filter((u) => u.uid !== currentUser.uid)
                              .map((admin) => (
                                <button
                                  key={admin.uid}
                                  type="button"
                                  onClick={() => {
                                    setShowColleaguePicker(false);
                                    void handleTriageAssign({
                                      uid: admin.uid,
                                      email: admin.email,
                                      displayName:
                                        admin.displayName || admin.email,
                                    });
                                  }}
                                  className="w-full text-left px-3 py-2 text-xs hover:bg-teal-50 flex items-center gap-2.5 transition-colors cursor-pointer"
                                >
                                  <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-800 text-[11px] font-bold flex items-center justify-center shrink-0">
                                    {(admin.displayName || admin.email)
                                      .slice(0, 1)
                                      .toUpperCase()}
                                  </div>
                                  <div className="overflow-hidden truncate">
                                    <p className="font-semibold text-slate-800 truncate">
                                      {admin.displayName || admin.email}
                                    </p>
                                    <p className="text-[10px] text-slate-400 truncate">
                                      {admin.email}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            {adminUsers.filter((u) => u.uid !== currentUser.uid)
                              .length === 0 && (
                              <p className="px-3 py-2 text-xs text-slate-400 italic">
                                No other active admins found.
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* 3. Hold Off / Decide Later */}
                    <button
                      type="button"
                      disabled={isUpdatingStatus}
                      onClick={() => setStage("detail")}
                      className="p-4 rounded-xl bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-300 shadow-xs transition-all flex flex-col items-center justify-center text-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center">
                        <Clock className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold">Hold Off</p>
                        <p className="text-[10px] text-slate-500">
                          Decide later &amp; view ticket
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Stage 1 Content Area */}
              <div className="p-6 sm:p-8 space-y-5 overflow-y-auto flex-1">
                <div className="text-xs text-slate-500">
                  Submitted by{" "}
                  <strong className="text-slate-700">
                    {issue.reporterName}
                  </strong>{" "}
                  ({issue.reporterEmail}) on{" "}
                  {new Date(issue.createdAt).toLocaleString()}
                </div>

                {/* Description Box */}
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-2">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Issue Description
                  </h3>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {issue.description}
                  </p>
                </div>

                {/* Expected Behavior */}
                {issue.expectedBehavior && (
                  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-2">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Expected Behavior
                    </h3>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                      {issue.expectedBehavior}
                    </p>
                  </div>
                )}

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Attachments ({attachments.length})
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {attachments.map((att, idx) => (
                        <button
                          key={att.path || idx}
                          type="button"
                          onClick={() => setPreviewAttachment(att)}
                          className="block w-full text-left rounded-xl overflow-hidden border border-slate-200 hover:border-teal-400 transition group cursor-pointer focus-visible:outline-2 focus-visible:outline-teal-500"
                          title={`Preview ${att.name}`}
                          aria-label={`Preview ${att.name}`}
                        >
                          <img
                            src={att.url}
                            alt={att.name}
                            className="w-full h-24 object-cover group-hover:scale-105 transition-transform"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Modal Header */}
              <div className="p-5 sm:p-6 border-b border-slate-200 bg-slate-50/80 flex items-start justify-between gap-4">
                <div className="space-y-1.5 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-md font-mono text-xs font-bold bg-teal-100 text-teal-800 border border-teal-200">
                      {issue.id}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border tracking-wide shadow-2xs ${getAppPillClasses(issue.appName)}`}
                    >
                      {issue.appName}
                    </span>
                    <CategoryBadge category={issue.category} size="md" />
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-semibold uppercase ${
                        issue.priority === "critical"
                          ? "bg-rose-100 text-rose-800"
                          : issue.priority === "high"
                            ? "bg-amber-100 text-amber-800"
                            : issue.priority === "medium"
                              ? "bg-sky-100 text-sky-800"
                              : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {issue.priority}
                    </span>
                  </div>
                  <h2
                    id="issue-detail-title"
                    className="text-lg sm:text-xl font-bold text-slate-900 leading-tight"
                  >
                    {issue.title}
                  </h2>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {isStaff &&
                    (issue.status === "new" || !issue.assigneeUid) && (
                      <button
                        type="button"
                        onClick={() => setStage("triage")}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 transition-colors cursor-pointer"
                        title="Open Triage View"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                        <span>Triage</span>
                      </button>
                    )}

                  {targetUrl && (
                    <a
                      href={targetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open page URL: ${targetUrl}`}
                      aria-label="Open page URL in new tab"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors shrink-0 focus-visible:outline-2 focus-visible:outline-teal-500"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close dialog"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* SLA, TTR, and Status Action Strip */}
              <div className="px-5 sm:px-6 py-2.5 sm:py-3 bg-slate-100/75 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-600 mr-0.5">
                    Status:
                  </span>
                  {isStaff ? (
                    <div
                      role="group"
                      aria-label="Ticket status"
                      className="flex flex-wrap items-center gap-1.5"
                    >
                      {STATUS_OPTIONS.map((opt) => {
                        const isActive = issue.status === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            aria-pressed={isActive}
                            data-testid={`status-btn-${opt.value}`}
                            disabled={isUpdatingStatus}
                            onClick={() => handleStatusButtonClick(opt.value)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                              isActive ? opt.activeClass : opt.inactiveClass
                            }`}
                          >
                            {isActive && <Check className="w-3 h-3" />}
                            <span>{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span
                      data-testid="status-badge-readonly"
                      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
                        STATUS_CONFIG[issue.status]?.badgeClass ||
                        "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {STATUS_CONFIG[issue.status]?.label || issue.status}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2.5">
                  {/* TTR (Time to Resolution) Badge */}
                  {ttrMetrics && (
                    <div
                      data-testid="ttr-badge"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-2xs"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Time to Resolution: {ttrMetrics.formatted}</span>
                    </div>
                  )}

                  {/* SLA Badge */}
                  {slaInfo && (
                    <div
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-semibold ${
                        slaInfo.isBreached
                          ? "bg-rose-100 text-rose-800"
                          : slaInfo.isWarning
                            ? "bg-amber-100 text-amber-800"
                            : "bg-teal-100 text-teal-800"
                      }`}
                    >
                      {slaInfo.isBreached ? (
                        <ShieldAlert className="w-3.5 h-3.5" />
                      ) : (
                        <Clock className="w-3.5 h-3.5" />
                      )}
                      <span>SLA: {slaInfo.text}</span>
                    </div>
                  )}
                </div>

                {/* Inline Confirmation Prompt */}
                {pendingStatusChange && (
                  <div
                    data-testid="status-confirm-banner"
                    className="w-full flex flex-wrap items-center justify-between gap-3 px-3.5 py-2 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs animate-in fade-in duration-150"
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>
                        Confirm marking ticket as{" "}
                        <strong className="font-bold uppercase">
                          {STATUS_CONFIG[pendingStatusChange]?.label ||
                            pendingStatusChange}
                        </strong>
                        ?
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        data-testid="confirm-status-cancel"
                        onClick={() => setPendingStatusChange(null)}
                        className="px-2.5 py-1 rounded-lg bg-white border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        data-testid="confirm-status-btn"
                        onClick={() =>
                          void executeStatusChange(pendingStatusChange)
                        }
                        className="px-2.5 py-1 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition shadow-2xs cursor-pointer"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Tabs */}
              <div className="flex items-center px-6 border-b border-slate-200 bg-white overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab("overview")}
                  className={`py-3 px-4 text-xs font-semibold border-b-2 transition-colors shrink-0 ${
                    activeTab === "overview"
                      ? "border-teal-600 text-teal-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Overview
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("comments")}
                  className={`py-3 px-4 text-xs font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 shrink-0 ${
                    activeTab === "comments"
                      ? "border-teal-600 text-teal-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>Comments ({sortedComments.length})</span>
                </button>
                {isStaff && (
                  <button
                    type="button"
                    onClick={() => setActiveTab("notes")}
                    className={`py-3 px-4 text-xs font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 shrink-0 ${
                      activeTab === "notes"
                        ? "border-amber-500 text-amber-800 bg-amber-50/50"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <Lock className="w-3.5 h-3.5 text-amber-600" />
                    <span>Internal Notes ({internalNotes.length})</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActiveTab("activity")}
                  className={`py-3 px-4 text-xs font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 shrink-0 ${
                    activeTab === "activity"
                      ? "border-teal-600 text-teal-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <History className="w-3.5 h-3.5" />
                  <span>Activity History ({activities.length})</span>
                </button>
              </div>

              {/* Tab Content Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* TAB 1: OVERVIEW */}
                {activeTab === "overview" && (
                  <div className="space-y-6 text-xs">
                    {/* Description */}
                    <div className="space-y-2">
                      <h3 className="font-bold text-slate-800 text-sm">
                        {issue.category === "bug"
                          ? "Description & Steps to Reproduce"
                          : "Feedback & Details"}
                      </h3>
                      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 whitespace-pre-wrap leading-relaxed font-sans">
                        {issue.description}
                      </div>
                    </div>

                    {/* Expected Behavior */}
                    {issue.expectedBehavior && (
                      <div className="space-y-2">
                        <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          <span>
                            What Should Have Happened (Expected Behavior)
                          </span>
                        </h3>
                        <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-200/80 text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {issue.expectedBehavior}
                        </div>
                      </div>
                    )}

                    {/* Screenshots & Attachments */}
                    {attachments.length > 0 && (
                      <div className="space-y-2">
                        <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                          <Paperclip className="w-3.5 h-3.5 text-teal-600" />
                          <span>
                            Screenshots & Attachments ({attachments.length})
                          </span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {attachments.map((att, idx) => (
                            <div
                              key={att.path || idx}
                              role="button"
                              tabIndex={0}
                              onClick={() => setPreviewAttachment(att)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setPreviewAttachment(att);
                                }
                              }}
                              title={`Click to preview ${att.name}`}
                              aria-label={`Preview ${att.name}`}
                              className="group relative block rounded-xl overflow-hidden border border-slate-200 bg-slate-50 hover:border-teal-500 transition-all shadow-xs cursor-pointer focus-visible:outline-2 focus-visible:outline-teal-500"
                            >
                              <img
                                src={att.url}
                                alt={att.name}
                                className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                              <div className="p-2.5 text-[10px] text-slate-600 bg-white border-t border-slate-100 flex items-center justify-between">
                                <span className="font-medium truncate max-w-[200px]">
                                  {att.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSafeAttachmentUrl(
                                      att.url,
                                      `${issue.id}_${att.name}`,
                                    );
                                  }}
                                  title="Open in new tab"
                                  aria-label="Open attachment in new browser tab"
                                  className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-teal-600 transition cursor-pointer"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* People & Assignment Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1.5">
                        <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
                          Reported By
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center text-xs">
                            {issue.reporterName.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">
                              {issue.reporterName}
                            </p>
                            <p className="text-[10px] text-slate-500">
                              {issue.reporterEmail}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-3.5 rounded-xl border border-slate-200 bg-white space-y-1.5">
                        <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
                          Assigned Admin
                        </span>
                        {canAssign ? (
                          <select
                            aria-label="Assign to team member"
                            value={issue.assigneeUid || ""}
                            onChange={(e) =>
                              void handleAssigneeChange(e.target.value)
                            }
                            className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-xs"
                          >
                            <option value="">Unassigned (Open Queue)</option>
                            {issue.assigneeUid &&
                              !adminUsers.some(
                                (a) => a.uid === issue.assigneeUid,
                              ) && (
                                <option value={issue.assigneeUid}>
                                  {issue.assigneeEmail || "Previous Assignee"}{" "}
                                  (Assigned)
                                </option>
                              )}
                            {adminUsers.map((a) => (
                              <option key={a.uid} value={a.uid}>
                                {a.displayName || a.email} ({a.email})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="font-semibold text-slate-800">
                            {issue.assigneeEmail || "Unassigned"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Lifecycle Timestamps */}
                    <div className="space-y-2">
                      <h3 className="font-bold text-slate-800 text-sm">
                        Lifecycle Timestamps
                      </h3>
                      <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 grid grid-cols-2 sm:grid-cols-4 gap-3 text-slate-600">
                        <div>
                          <span className="text-slate-400 block text-[10px]">
                            Created:
                          </span>
                          <span className="font-medium text-slate-800">
                            {new Date(issue.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">
                            Updated:
                          </span>
                          <span className="font-medium text-slate-800">
                            {new Date(issue.updatedAt).toLocaleString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">
                            Resolved:
                          </span>
                          <span className="font-medium text-slate-800">
                            {issue.resolvedAt
                              ? new Date(issue.resolvedAt).toLocaleString()
                              : "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">
                            Closed:
                          </span>
                          <span className="font-medium text-slate-800">
                            {issue.closedAt
                              ? new Date(issue.closedAt).toLocaleString()
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* System Environment & Metadata */}
                    <div className="space-y-2">
                      <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                        <Monitor className="w-4 h-4 text-teal-600" />
                        <span>Client Diagnostic Telemetry</span>
                      </h3>
                      <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 grid grid-cols-1 sm:grid-cols-2 gap-4 text-slate-600">
                        {/* Browser & OS */}
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-semibold">
                            Browser
                          </span>
                          <span className="font-bold text-slate-800">
                            {issue.sourceMetadata?.browser || "Unknown"}
                          </span>
                        </div>

                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-semibold">
                            Operating System
                          </span>
                          <span className="font-bold text-slate-800">
                            {issue.sourceMetadata?.os || "Unknown"}
                          </span>
                        </div>

                        {/* Network Status */}
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                            <Wifi className="w-3 h-3 text-slate-400" />
                            <span>Network Status</span>
                          </span>
                          <span className="font-medium text-slate-800">
                            {formatNetworkStatus(issue.sourceMetadata?.network)}
                          </span>
                        </div>

                        {/* IP Address */}
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                            <Globe className="w-3 h-3 text-slate-400" />
                            <span>IP Address</span>
                          </span>
                          {issue.sourceMetadata?.ipAddress ? (
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-slate-800 font-semibold">
                                {issue.sourceMetadata.ipAddress}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleCopyIp(issue.sourceMetadata.ipAddress!)
                                }
                                aria-label="Copy IP"
                                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                              >
                                {copiedIp ? (
                                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <Copy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-400 italic">
                              Not captured
                            </span>
                          )}
                        </div>

                        {/* Screen & Viewport */}
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-semibold">
                            Screen & Viewport
                          </span>
                          <span className="font-medium text-slate-800 font-mono">
                            {issue.sourceMetadata?.screenResolution ||
                              "Unknown"}
                            {issue.sourceMetadata?.viewportSize
                              ? ` (Viewport: ${issue.sourceMetadata.viewportSize})`
                              : ""}
                          </span>
                        </div>

                        {/* Context URL */}
                        {issue.sourceMetadata?.url && (
                          <div className="sm:col-span-2 truncate">
                            <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-semibold">
                              Page URL
                            </span>
                            <a
                              href={issue.sourceMetadata.url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-teal-600 underline inline-flex items-center gap-1 max-w-full truncate"
                            >
                              <span className="truncate">
                                {issue.sourceMetadata.url}
                              </span>
                              <ExternalLink className="w-3 h-3 shrink-0" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: COMMENTS */}
                {activeTab === "comments" && (
                  <div className="space-y-4">
                    {/* Post Comment Input (at top of chats) */}
                    <form onSubmit={handlePostComment} className="space-y-1.5">
                      <div className="flex gap-2 items-end">
                        <textarea
                          value={commentInput}
                          onChange={(e) => setCommentInput(e.target.value)}
                          onKeyDown={handleCommentKeyDown}
                          placeholder="Add a comment... (Shift + Enter for new line)"
                          rows={2}
                          className="flex-1 px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y min-h-[42px] max-h-48 leading-relaxed"
                        />
                        <button
                          type="submit"
                          disabled={isSending || !commentInput.trim()}
                          className="btn-animate px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 h-[38px] shrink-0 cursor-pointer"
                        >
                          <Send className="w-3.5 h-3.5" />
                          <span>Send</span>
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 px-0.5">
                        <span>
                          <kbd className="font-sans px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] text-slate-500">
                            Shift
                          </kbd>{" "}
                          +{" "}
                          <kbd className="font-sans px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] text-slate-500">
                            Enter
                          </kbd>{" "}
                          for soft return &bull;{" "}
                          <kbd className="font-sans px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] text-slate-500">
                            Enter
                          </kbd>{" "}
                          to send
                        </span>
                      </div>
                    </form>

                    {/* Sort Order Toolbar */}
                    <div className="flex items-center justify-between pt-1 pb-0.5 text-xs text-slate-500">
                      <span className="font-semibold">
                        {sortedComments.length} comment
                        {sortedComments.length === 1 ? "" : "s"}
                      </span>
                      <button
                        type="button"
                        data-testid="comment-sort-toggle"
                        onClick={() =>
                          setCommentSortOrder((prev) =>
                            prev === "desc" ? "asc" : "desc",
                          )
                        }
                        aria-label={`Sort comments: currently ${commentSortOrder === "desc" ? "newest first" : "oldest first"}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                      >
                        <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" />
                        <span>
                          {commentSortOrder === "desc"
                            ? "Newest first"
                            : "Oldest first"}
                        </span>
                      </button>
                    </div>

                    <div className="space-y-3">
                      {sortedComments.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-8">
                          No comments yet. Start the conversation above.
                        </p>
                      ) : (
                        sortedComments.map((c) => {
                          const isUser = c.authorRole === "end_user";
                          const alignmentClass = isUser ? "ml-8" : "mr-8";
                          const styleClass = isUser
                            ? "border-slate-200 bg-white shadow-sm"
                            : "border-blue-100 bg-blue-50/40";
                          const badgeClass = isUser
                            ? "bg-slate-100 text-slate-600"
                            : "bg-blue-100 text-blue-700";

                          return (
                            <div
                              key={c.id}
                              className={`p-3.5 rounded-xl border space-y-1.5 text-xs ${alignmentClass} ${styleClass}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-slate-900">
                                    {c.authorName}
                                  </span>
                                  <span
                                    className={`px-1.5 py-0.2 rounded text-[10px] font-semibold ${badgeClass}`}
                                  >
                                    {c.authorRole}
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-500 font-medium">
                                  {formatCommentDateTime(c.createdAt)}
                                </span>
                              </div>
                              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed break-words">
                                {c.content}
                              </p>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* TAB 3: INTERNAL NOTES */}
                {activeTab === "notes" && isStaff && (
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs flex items-center gap-2">
                      <Lock className="w-4 h-4 shrink-0 text-amber-600" />
                      <span>
                        Internal notes are private and only visible to IT and
                        Department Admins. Ticket reporters cannot view these
                        notes.
                      </span>
                    </div>

                    <div className="space-y-3">
                      {sortedInternalNotes.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-6">
                          No internal notes yet.
                        </p>
                      ) : (
                        sortedInternalNotes.map((n) => (
                          <div
                            key={n.id}
                            className="p-3.5 rounded-xl border border-amber-200 bg-amber-50/40 space-y-1 text-xs"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-amber-900">
                                {n.authorName}
                              </span>
                              <span className="text-[10px] text-amber-700 font-medium">
                                {formatCommentDateTime(n.createdAt)}
                              </span>
                            </div>
                            <p className="text-slate-800 whitespace-pre-wrap leading-relaxed break-words">
                              {n.content}
                            </p>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Add Internal Note Input */}
                    <form
                      onSubmit={handlePostNote}
                      className="pt-2 space-y-1.5"
                    >
                      <div className="flex gap-2 items-end">
                        <textarea
                          value={noteInput}
                          onChange={(e) => setNoteInput(e.target.value)}
                          onKeyDown={handleNoteKeyDown}
                          placeholder="Add internal triage note... (Shift + Enter for new line)"
                          rows={2}
                          className="flex-1 px-3 py-2 text-xs bg-white border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y min-h-[42px] max-h-48 leading-relaxed"
                        />
                        <button
                          type="submit"
                          disabled={isSending || !noteInput.trim()}
                          className="btn-animate px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 h-[38px] shrink-0 cursor-pointer"
                        >
                          <Lock className="w-3.5 h-3.5" />
                          <span>Save Note</span>
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-400 px-0.5">
                        <span>
                          <kbd className="font-sans px-1 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                            Shift
                          </kbd>{" "}
                          +{" "}
                          <kbd className="font-sans px-1 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                            Enter
                          </kbd>{" "}
                          for soft return &bull;{" "}
                          <kbd className="font-sans px-1 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                            Enter
                          </kbd>{" "}
                          to save
                        </span>
                      </div>
                    </form>
                  </div>
                )}

                {/* TAB 4: ACTIVITY HISTORY (AUDIT TRAIL) */}
                {activeTab === "activity" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                        <History className="w-4 h-4 text-teal-600" />
                        <span>Audit Trail & Activity Log</span>
                      </h3>
                      <span className="text-[11px] text-slate-400">
                        {activities.length} event
                        {activities.length === 1 ? "" : "s"}
                      </span>
                    </div>

                    {activities.length === 0 ? (
                      <div className="text-center py-10 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                        <History className="w-8 h-8 text-slate-300 mx-auto" />
                        <p className="text-xs font-medium text-slate-600">
                          No activity recorded yet
                        </p>
                        <p className="text-[10px] text-slate-400">
                          Ticket changes and assignments will appear here
                          automatically.
                        </p>
                      </div>
                    ) : (
                      <div className="relative border-l-2 border-slate-200 ml-4 pl-4 space-y-5 my-2">
                        {activities.map((act) => (
                          <div key={act.id} className="relative group">
                            {/* Timeline dot */}
                            <div className="absolute -left-[23px] top-1 w-3.5 h-3.5 rounded-full bg-white border-2 border-teal-600" />

                            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-1 hover:border-slate-300 transition-colors">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-slate-900">
                                    {act.actorName}
                                  </span>
                                  <span className="text-[10px] text-slate-500">
                                    ({act.actorEmail})
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-400 font-medium">
                                  {formatActivityTimestamp(act.timestamp)}
                                </span>
                              </div>

                              <p className="text-slate-700 font-medium pt-0.5">
                                {act.summary}
                              </p>

                              {/* Optional status/assignment change diff pills */}
                              {act.changes?.status && (
                                <div className="pt-1 flex items-center gap-1.5 text-[11px]">
                                  <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">
                                    {act.changes.status.from}
                                  </span>
                                  <span className="text-slate-400">→</span>
                                  <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-800 font-mono font-bold">
                                    {act.changes.status.to}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {previewAttachment && (
        <ImageLightboxModal
          isOpen={!!previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          imageUrl={previewAttachment.url}
          imageName={previewAttachment.name}
          issueId={issue?.id}
        />
      )}
    </>
  );
};

// Aliased export for backwards compatibility
export const IssueDetailDrawer = IssueDetailModal;
